"use client";

import { useEffect, useRef } from "react";
import { domainEventInvalidateKey } from "@/hooks/domainEventInvalidateKey";
import { getDomainEventScopeKey, useDomainEventStore, type DomainEvent, type DomainEventScope, type DomainEventType } from "@/stores/useDomainEventStore";
const DEFAULT_DEBOUNCE_MS = 200;
export interface UseDomainEventInvalidationOptions {
  scope: DomainEventScope;
  eventTypes: DomainEventType[];
  entityTypesAffected?: string[];
  onInvalidate: (event: DomainEvent) => void;
  debounceMs?: number;
  enabled?: boolean;
}

/**
 * Invokes a callback when matching domain events arrive for the given scope(s).
 *
 * Debounces bursts, but coalesces by {@link domainEventInvalidateKey} so distinct
 * entity ids in the same window each flush (latest event per key wins).
 */
export function useDomainEventInvalidation({
  scope,
  eventTypes,
  entityTypesAffected,
  onInvalidate,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true
}: UseDomainEventInvalidationOptions) {
  const scopeKey = getDomainEventScopeKey(scope);
  const projectScopeKey = scope.projectId ? getDomainEventScopeKey({
    projectId: scope.projectId,
    conversationId: null
  }) : null;
  const revision = useDomainEventStore(s => s.revisionByScopeKey[scopeKey] ?? 0);
  const latestEvent = useDomainEventStore(s_0 => s_0.latestEventByScopeKey[scopeKey]);
  const projectRevision = useDomainEventStore(s_1 => projectScopeKey ? s_1.revisionByScopeKey[projectScopeKey] ?? 0 : 0);
  const projectLatestEvent = useDomainEventStore(s_2 => projectScopeKey ? s_2.latestEventByScopeKey[projectScopeKey] : undefined);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingByKeyRef = useRef<Map<string, DomainEvent>>(new Map());
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;
  const debounceMsRef = useRef(debounceMs);
  debounceMsRef.current = debounceMs;
  const eventTypeSet = useRef(new Set(eventTypes));
  eventTypeSet.current = new Set(eventTypes);
  const pendingMap = (): Map<string, DomainEvent> => {
    // Fast Refresh can leave a non-Map in the ref after shape changes; recreate.
    if (!(pendingByKeyRef.current instanceof Map)) {
      pendingByKeyRef.current = new Map();
    }
    return pendingByKeyRef.current;
  };
  const flushPending = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingMap();
    const events = [...pending.values()];
    pending.clear();
    for (const event of events) {
      onInvalidateRef.current(event);
    }
  };
  const clearPending = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingMap().clear();
  };
  const scheduleInvalidate = (event_0: DomainEvent) => {
    pendingMap().set(domainEventInvalidateKey(event_0), event_0);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      flushPending();
    }, debounceMsRef.current);
  };
  const matches = (event_1?: DomainEvent) => {
    if (!event_1) {
      return false;
    }
    if (!eventTypeSet.current.has(event_1.eventType)) {
      return false;
    }
    if (!entityTypesAffected?.length) {
      return true;
    }
    return event_1.entityTypesAffected?.some(type => entityTypesAffected.includes(type)) ?? false;
  };
  useEffect(() => {
    if (!enabled) {
      clearPending();
      return;
    }
    if (!latestEvent || !matches(latestEvent)) {
      return;
    }
    scheduleInvalidate(latestEvent);
    // Intentionally no timer cleanup: revision churn must not drop buffered
    // entity keys. scheduleInvalidate slides the debounce window instead.
  }, [enabled, latestEvent, revision, entityTypesAffected?.join(",")]);
  useEffect(() => {
    if (!enabled || !projectLatestEvent || !matches(projectLatestEvent)) {
      return;
    }
    scheduleInvalidate(projectLatestEvent);
  }, [enabled, projectLatestEvent, projectRevision, entityTypesAffected?.join(",")]);
  useEffect(() => {
    return () => {
      flushPending();
    };
  }, []);
}